import { ApplicationState } from 'ansok/redux/ducks/application'
import { LoginState } from 'ansok/redux/ducks/login'
import { ConfigType, LocaleInterface } from 'types'

export type ContextType = {
  config?: ConfigType
  locale?: LocaleInterface
}

export type StepType = {
  all: string[]
  currentIndex: number
  next: string | null
  prev: string | null
}

export type ApplicationFormStepProps = {
  formPath?: string
  formSteps?: StepType
  onNextStep?: () => void
  initialValues?: any
  attachments?: any
  login?: LoginState
  application?: ApplicationState
  setAttachment?: (attachment, value) => any
}
export type Applicant = {
  applicantId: string
  personalId?: string
  firstName?: string
  lastName?: string
}

export enum CustomerType {
  ENSKILD_FIRMA = 'ENSKILD_FIRMA',
  STAT_LANDSTING_KOMMUN = 'STAT_LANDSTING_KOMMUN',
  AKTIEBOLAG = 'AKTIEBOLAG',
  EKONOMISK_FORENING = 'EKONOMISK_FORENING',
  IDEELL_FORENING_STIFTELSE = 'IDEELL_FORENING_STIFTELSE',
  HANDELSBOLAG_KOMMANDITBOLAG = 'HANDELSBOLAG_KOMMANDITBOLAG',
}

export enum AttachmentType {
  ARSREDOVISING = 'ÅRSREDOVISNING',
  REGISTRERINGSBEVIS = 'REGISTRERINGSBEVIS',
  STADGAR = 'STADGAR',
  FULLMAKT_ATTESTFORTECKNING = 'FULLMAKT_ATTESTFÖRTECKNING',
}

export enum DiscountListQueryEnum {
  ORG_KIM_ID = 'org-kim-id',
  DISCOUNT_ID = 'discount-id',
  ORG_ACCOUNT_ID = 'org-account-id',
  INVOICE_RECIPIENT_ID = 'invoice-recipient-id',
}

export type DiscountListQuery =
  | 'org-kim-id'
  | 'org-account-id'
  | 'invoice-recipient-id'

export type DiscountCommentsQuery =
  | 'discount-id'
  | 'org-account-id'
  | 'invoice-recipient-id'

// TODO: BE constant, shouldn't be handleded on FE, JIRA pending for this, remove when posibile
export enum NationalCustomer {
  NATIONAL_CUSTOMER = 'National Customer',
}

export enum DiscountType {
  VOLUME = 'VOLUME',
  FIXED = 'FIXED',
  NONE = 'NONE',
}

type IncludesDiscountType = keyof typeof DiscountType

export enum DiscountStatus {
  ACTIVE = 'ACTIVE',
  PENDING = 'PENDING',
  HISTORY = 'HISTORY',
}

export enum InheritedFrom {
  ORGANIZATION = 'ORGANIZATION',
  ORG_ACCOUNT = 'ORG_ACCOUNT',
  NONE = 'NONE',
}

type IncludesInheritedFrom = keyof typeof InheritedFrom
type IncludesDiscountStatus = keyof typeof DiscountStatus

export enum DiscountCustomerType {
  LOCAL = 'LOCAL',
  NATIONAL = 'NATIONAL',
}

type IncludesDiscountCustomerType = keyof typeof DiscountCustomerType

export enum CustomerActivity {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

type IncludesCustomerActivity = keyof typeof CustomerActivity

export type Customer = {
  orgNumber: string
  type: CustomerType
}

export enum ApplicationStatus {
  OPEN = 'OPEN',
  WAIT_SIGN = 'WAIT_SIGN',
  WAIT_RESIGN = 'WAIT_RESIGN',
  SIGNED = 'SIGNED',
  REWORK = 'REWORK',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  CANCELLED = 'CANCELLED',
}

export enum SigningAssignmentStatus {
  CREATED = 'CREATED',
  COMPLETED = 'COMPLETED',
  REJECTED = 'REJECTED',
  DELETED = 'DELETED',
}

export type StatusHistory = {
  status: ApplicationStatus
  ts: string
}

export type SigningData = {
  created: string
  signingAssignmentId: string
  status: SigningAssignmentStatus
  tasks: SigningTask[]
}

export type SigningTask = {
  personalId: string
  signUrl: string
  completedTs: string
  signatoryName: string
  signedDocuments: SignedDocument[]
}

export type SigningTaskInLifecycle = {
  ts: string
  signatoryName: string
  personalId: string
}

export type SignedDocument = {
  originalDocumentId: string
  signedDocumentId: string
}

export type AttachmentPost = {
  data: string
  type: string
  mimeType: string
  filename: string
}

export type Attachment = {
  id: string
  type: AttachmentType
  mimeType: string
  filename: string
}

export type ApplicantFormData = {
  email?: string
  estimatedBudget?: string
}
export type CustomerFormData = {
  name: string
  foodCompany?: boolean
}

export type AdministratorFieldType = {
  firstName: string
  lastName: string
  personalId: string
  email: string
  phone: string
}
export type AdministratorFormData = {
  administrator: AdministratorFieldType
}
export type SignatoryFieldType = {
  firstName: string
  lastName: string
  personalId: string
  email: string
  phone: string
}
export type SignatoriesFormData = {
  signatories: SignatoryFieldType[]
}
export type KeyFiguresFieldType = {
  revenue: number
  employees: number
  staffCosts: number
  turnover: number
  result: number
  capital: number
  newCompany: string
}
export type KeyFiguresFormData = {
  keyFigures: KeyFiguresFieldType
}
export type PrincipalsFieldType = {
  noPrincipal: boolean
  principalsList: PrincipalFieldType[]
}
export type PrincipalsFormData = {
  principals: PrincipalsFieldType
}
export type PrincipalFieldType = {
  name: string
  personalId: string
  share: string
  role: string
  PEP: boolean
}

export type AttachmentFieldType = {
  [name: string]: any
}
export type AttachmentFormData = {
  attachments: AttachmentFieldType
}

type FormDataBase = {
  applicant: ApplicantFormData
  customer: CustomerFormData
  administrator?: AdministratorFieldType
  signatories?: SignatoryFieldType[]
}

export type FormData = FormDataBase & {
  keyFigures?: KeyFiguresFieldType
  orgInfo?: { estimatedBudget: string }
}

export type BackendFormData = FormDataBase & {
  properties?: { [key: string]: string }
}

export type Application = {
  id: string
  applicant: Applicant
  customer: Customer
  created: string
  status: ApplicationStatus
  statusHistories?: StatusHistory[]
  attachments?: Attachment[]
  formData: FormData
}

export type AdminApplication = Application & {
  adminComments?: AdminComment[]
  signingData?: SigningData[]
}

export type AdminApplicationSummary = {
  id: string
  customerOrgNo: string
  customerType: CustomerType
  customerName: string
  applicantName: string
  created: string
  status: ApplicationStatus
}

export type AdminOrganizationLimit = {
  orgKimId: number
  orgNumber: string
  orgName: string
  riskLevel: RiskLevel
  blocked: boolean
  purchaseLimit: number | null
  bookedAmount: number
  reservedAmount: number
  availableAmount: number | null
  editActive?: boolean
}

export const maxPurchaseLimitsForDifferentRiskLevelsOnApplication = {
    LOW_RISK: 150000,
    MEDIUM_RISK: 30000,
    HIGH_RISK: 5000,
  }

  export const maxPurchaseLimitsForDifferentRiskLevelsOnAdmin = {
    LOW_RISK: 250000,
    MEDIUM_RISK: 100000,
    HIGH_RISK: 15000,
  }


export enum RiskLevel {
  NO_RISK = 'NO_RISK',
  LOW_RISK = 'LOW_RISK',
  MEDIUM_RISK = 'MEDIUM_RISK',
  HIGH_RISK = 'HIGH_RISK',
}

export type OrganisationHierarchy = {
  orgKimId: string
  orgName: string
  activeOrgDiscount?: CustomerDiscount
  orgAccounts: OrgAccountHierarchy[]
}

export type OrgAccountHierarchy = {
  activeOrgAccountDiscount?: CustomerDiscount | any
  invoiceRecipients: InvoiceRecipientHiearchy
  orgAccountId: string
  orgAccountName: string
  orgAccountStatus: string
}

export type InvoiceRecipientHiearchy = {
  activeInvoiceRecipientDiscount?: CustomerDiscount
  invoiceRecipientId: string
  invoiceRecipientName: string
  invoiceRecipientStatus: string
}

export type OrganisationHierarchyItem = {
  title: string
  orgKimId: number
  queryType: DiscountListQuery
  exceptions: string
  children: OrganisationHierarchyItem[]
}
export type OrgHierarchy = OrganisationHierarchyItem[]

export type Comment = {
  created: string
  comment: string
  authorId: string
  authorName: string
}

export type CustomerDetails = {
  type: string
  id: number
  name: string
  excluded?: boolean
  active?: IncludesCustomerActivity
}

export type CustomerDiscount = {
  id?: string
  startDate: string
  endDate?: string
  serviceFeeDiscount?: string | number
  discountType?: IncludesDiscountType
  fixedDiscount?: string
  volumeDiscount?: string
  standAlone: boolean
  coopRegion: string
  customerType: IncludesDiscountCustomerType
  status?: IncludesDiscountStatus
  inheritedFrom?: IncludesInheritedFrom
  exclude?: boolean
}

export type CreateCustomerDiscount = {
  orgKimId?: string
  orgAccountId?: string
  invoiceRecipientId?: string
  createDiscount: CustomerDiscount
}

export type DiscountDetails = {
  customerDetails?: CustomerDetails
  activeDiscount?: CustomerDiscount
  pendingDiscounts?: CustomerDiscount[]
  discountHistory?: CustomerDiscount[]
}

export type DiscountDetailsResposne = {
  discountDetails: DiscountDetails
}

export type OrganizationPersonListItem = {
  firstName: string
  lastName: string
  personalId: string
}

export type OrganizationAdminListItem = {
  firstName: string
  lastName: string
  email: string
  personalId: string
  impersonationLink: string
}

export type OrganizationDiscountItem = {
  orgKimId: number
  orgNumber: string
  orgName: string
  orgActive: boolean
  activeOrgDiscount?: boolean
  orgAdmins?: OrganizationAdminListItem[]
}

export type OrgDiscountsResponse = {
  organizations: OrganizationDiscountItem[]
}

export type OrganizationLimitsResponseType = {
  organizationLimits: AdminOrganizationLimit[]
}

export enum SetOrgLimitActionsType {
  EDIT = 'EDIT',
  SAVE = 'SAVE',
  DISCARD = 'DISCARD',
}

export type AdminComment = {
  created: string
  comment: string
  authorId: string
  authorName: string
}

export type AdminOrganizationComment = {
  comment: string
  createdAt: string
  id: string
  orgNumber: string
  userId: string
}

export type DiscountComment = {
  commentId: string
  text: string
  discountRef?: string
  userId?: string
  createdAt: string
}

export type ApplicationSummary = {
  id: string
  customerOrgNo: string
  customerType: CustomerType
  customerName: string
  created: string
  status: ApplicationStatus
}

export enum ApplicationErrorType {
  APPLICATION_IN_PROGRESS = 'APPLICATION_IN_PROGRESS',
  ORGANIZATION_ALREADY_CUSTOMER = 'ORGANIZATION_ALREADY_CUSTOMER',
}

export enum ErrorType {
  ALERT = 'ALERT',
  DESCRIPTIVE = 'DESCRIPTIVE',
}

export enum OrganizationCommentsErrorCodes {
  COMMENTS_NOT_FOUND = 'COMMENTS_NOT_FOUND',
}

export type AdminListApplicationsType = {
  status: ApplicationStatus
  ['org-number']: string
  sort: string
}

export type OrgLimitFiltersType = {
  ['org-number']: string
  ['org-name']: string
  sort: string
}
export type OrgDiscountsFiltersType = {
  ['org-number']: string
  ['org-name']: string
  ['first-last-name']: string
  sort: string
}

export enum OrganizationApplicationPropertyMap {
  ORG_INFO_ESTIMATED_BUDGET = 'orgInfo_estimatedBudget',
  KEY_FIGURES = 'keyFigures',
}

export type SignDocument = {
  docId: string
  mimeType: string
  data: string
  filename: string
}

export type SignDocumentsResponse = {
  signDocuments: SignDocument[]
}
